import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/common/Button';
import { ParticleBackground } from '../components/ParticleBackground';
import { withPageTransition, PageTransitionItem } from '../utils/withTransition';

const PaymentResultPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState<boolean>(true);
  
  useEffect(() => {
    // Determine if this is a success or error page based on URL path
    const path = location.pathname.toLowerCase();
    const isSuccessPage = path.includes('success') && !path.includes('error');
    setIsSuccess(isSuccessPage);
    
    // Log for debugging
    console.log(`Payment result page loaded: ${path}, isSuccess: ${isSuccessPage}`);
    
    // Auto-close this page after 10 seconds if it was opened as a popup
    const autoCloseTimer = setTimeout(() => {
      if (window.opener && !window.opener.closed) {
        window.close();
      }
    }, 5000);
    
    return () => clearTimeout(autoCloseTimer);
  }, [location.pathname]);
  
  // Handle back to dashboard click
  const handleBackToDashboard = () => {
    // If this is a popup, try to close it and notify opener
    if (window.opener && !window.opener.closed) {
      try {
        // Try to notify opener about completion
        if (window.opener.postMessage) {
          window.opener.postMessage({ 
            type: 'PAYMENT_COMPLETED', 
            success: isSuccess 
          }, '*');
        }
      } catch (e) {
        console.error('Error communicating with opener window:', e);
      }
      window.close();
    } else {
      // Navigate back to dashboard if not a popup
      navigate('/dashboard');
    }
  };
  
  // Custom background gradient styles
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };
  
  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark flex items-center justify-center" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      
      <PageTransitionItem index={1}>
        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark p-8 max-w-lg w-full mx-4 text-center backdrop-blur-sm shadow-sm">
          {isSuccess ? (
            <>
              <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold mb-3 text-text-primary-light dark:text-text-primary-dark">
                Payment Successful!
              </h1>
              <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6 text-base">
                Your payment has been processed successfully. Thank you for your purchase!
              </p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-2xl font-bold mb-3 text-text-primary-light dark:text-text-primary-dark">
                Payment Failed
              </h1>
              <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6 text-base">
                We couldn't process your payment. Please try again or contact support if the issue persists.
              </p>
            </>
          )}
          
          <div className="flex justify-center">
            <Button
              variant={isSuccess ? "primary" : "secondary"}
              onClick={handleBackToDashboard}
            >
              {isSuccess ? "Yaay! Continue" : "Return to Dashboard"}
            </Button>
          </div>
          
          {/* Auto-close message if in popup */}
          {window.opener && (
            <p className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark mt-6">
              This window will automatically close in 5 seconds.
            </p>
          )}
        </div>
      </PageTransitionItem>
    </div>
  );
};

export default withPageTransition(PaymentResultPage); 